import React, { useEffect, useRef } from 'react';

/**
 * Full-cover looping video backdrop with a scroll-driven parallax drift.
 * The poster image shows instantly (and is the fallback if the video can't
 * load), so the section is never empty. Motion respects reduced-motion.
 */
export const VideoBackdrop = ({
  src,
  poster,
  className,
  zoomOnScroll = false,
}: {
  src: string;
  poster: string;
  className?: string;
  /** Scrub the zoom level with scroll position (Wolverine-style). */
  zoomOnScroll?: boolean;
}): React.JSX.Element => {
  const vidRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const vid = vidRef.current;
    if (!vid) return;
    const section = vid.closest('section, header');
    if (!section) return;

    // Play only while the section is on screen (saves battery + guarantees
    // autoplay kicks in even for below-the-fold sections).
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          void vid.play().catch(() => {});
        } else {
          vid.pause();
        }
      },
      { threshold: 0.05 },
    );
    io.observe(section);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return () => io.disconnect();
    }

    let raf = 0;
    const update = (): void => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      if (zoomOnScroll) {
        // traveled: 0 when the section's top enters the bottom of the viewport,
        // 1 when its bottom leaves the top — scrub the zoom across that range.
        const traveled = Math.max(
          0,
          Math.min(1, (vh - rect.top) / (vh + rect.height)),
        );
        const scale = 1.12 + traveled * 0.3; // zoom in as you scroll through
        const shift = rect.top * -0.06;
        vid.style.transform = `scale(${scale.toFixed(3)}) translate3d(0, ${shift.toFixed(1)}px, 0)`;
      } else {
        // parallax: video drifts at ~12% of scroll speed while section is in view
        const shift = rect.top * -0.12;
        vid.style.transform = `scale(1.22) translate3d(0, ${shift.toFixed(1)}px, 0)`;
      }
    };
    const loop = (): void => {
      update();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [zoomOnScroll]);

  return (
    <div className={className}>
      <video
        ref={vidRef}
        className="videobd-vid"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={poster}
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
};
